import React, { Component } from "react";
import axios from "axios";
import * as config from "../../config";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
interface Record {
  [key: string]: any;
}

interface JVEPProps {
  jwtToken: string;
}

interface State {
  records: Record[];
  selectedTopic: string;
  selectedScenarioId: string;
  selectedRecord: Record | null;
  initialRecord: Record | null;
  isEditing: boolean;
  notification: string | null;
}

class JSONViewEditPage extends Component<JVEPProps, State> {
  constructor(props: JVEPProps) {
    super(props);
    this.state = {
      records: [],
      selectedTopic: "",
      selectedScenarioId: "",
      selectedRecord: null,
      initialRecord: null,
      isEditing: false,
      notification: null,
    };
  }

  componentDidMount() {
    this.fetchRecords();
  }

  fetchRecords = async () => {
    try {
      const response = await axios.get(
        `${config.rootUrl}/v1/simulator/api/get-records`,
        {
          headers: {
            "x-api-key": config.apiKey,
            Authorization: "Bearer " + this.props.jwtToken,
          },
        }
      );
      this.setState({ records: response.data.records });
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  handleTopicChange = (topic: string) => {
    this.setState({
      selectedTopic: topic,
      selectedScenarioId: "",
      selectedRecord: null,
      initialRecord: null,
    });
  };

  handleScenarioIdChange = (scenarioId: string) => {
    const { records, selectedTopic } = this.state;
    const record = records.find(
      (rec) =>
        rec["Topic Name"].S === selectedTopic &&
        rec["Scenario ID"].S === scenarioId
    );

    this.setState({
      selectedScenarioId: scenarioId,
      selectedRecord: record || null,
      initialRecord: record || null,
      isEditing: false,
    });
  };

  handleEdit = (opts: any) => {
    const { selectedRecord } = this.state;
    if (!selectedRecord) return;
    const updatedRecord = { ...selectedRecord, ...opts.src };
    this.setState({ selectedRecord: updatedRecord, isEditing: true });
  };

  handleSave = async () => {
    const { selectedRecord, initialRecord, selectedTopic, selectedScenarioId } =
      this.state;

    if (!selectedRecord || !initialRecord) {
      this.setNotification("Error: No record selected or initialized.");
      return;
    }

    if (
      selectedRecord["Topic Name"].S !== initialRecord["Topic Name"].S ||
      selectedRecord["Scenario ID"].S !== initialRecord["Scenario ID"].S
    ) {
      this.setNotification("Error: Please use Save As to create a new record");
      return;
    }

    this.setState({ isEditing: false });

    try {
      const encodedTopic = encodeURIComponent(selectedTopic);
      const response = await axios.put(
        `${config.rootUrl}/v1/simulator/api/update-record/${encodedTopic}/${selectedScenarioId}`,
        selectedRecord,
        {
          headers: {
            "x-api-key": config.apiKey,
            Authorization: "Bearer " + this.props.jwtToken,
          },
        }
      );
      console.log("Updated record:", response.data.updatedRecord);
      this.setNotification("Record edited successfully!");
      this.fetchRecords();
    } catch (error) {
      console.error("Error updating record:", error);
      this.setNotification("Error updating record. Please try again.");
    }
  };

  handleSaveAs = async () => {
    const { selectedRecord, initialRecord } = this.state;

    if (!selectedRecord || !initialRecord) {
      this.setNotification(
        "Error: No record to save. Please edit a record before saving."
      );
      return;
    }

    if (
      selectedRecord["Topic Name"].S === initialRecord["Topic Name"].S &&
      selectedRecord["Scenario ID"].S === initialRecord["Scenario ID"].S
    ) {
      this.setNotification(
        "Error: Cannot save as the same record. Please edit the existing record instead."
      );
      return;
    }

    if (selectedRecord["Topic Name"].S !== initialRecord["Topic Name"].S) {
      this.setNotification("Error: Cannot change Topic Name");
      return;
    }

    try {
      const response = await axios.post(
        `${config.rootUrl}/v1/simulator/api/save-as-record`,
        selectedRecord,
        {
          headers: {
            "x-api-key": config.apiKey,
            Authorization: "Bearer " + this.props.jwtToken,
          },
        }
      );
      console.log("New record saved:", response.data.newRecord);
      this.setNotification("Record saved as new!");
      this.fetchRecords();
      this.handleCancel();
    } catch (error) {
      console.error("Error saving record as new:", error);
      this.setNotification("Error saving record as new. Please try again.");
    }
  };

  handleCancel = () => {
    this.setState((prevState) => ({
      selectedRecord: prevState.initialRecord,
    }));
  };

  setNotification = (message: string) => {
    this.setState({ notification: message });
    setTimeout(() => {
      this.setState({ notification: null });
    }, 3000);
  };

  render() {
    const {
      records,
      selectedTopic,
      selectedScenarioId,
      selectedRecord,
      notification,
    } = this.state;

    return (
      <div style={{ maxWidth: "90%", margin: "0 auto", padding: "20px" }}>
        <h1 style={{ fontSize: "28px", marginBottom: "20px" }}>
          JSON View and Edit Page
        </h1>
        {notification && (
          <div
            style={{
              backgroundColor: notification.includes("Error")
                ? "#dc3545"
                : "#28a745",
              color: "#fff",
              padding: "10px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            {notification}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ flex: 1, marginRight: "10px" }}>
            <label
              htmlFor="topic"
              style={{
                display: "block",
                marginBottom: "10px",
                fontSize: "20px",
              }}
            >
              Select Topic:
            </label>
            <select
              id="topic"
              value={selectedTopic}
              onChange={(e) => this.handleTopicChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <option value="">Select Topic</option>
              {records
                .map((record) => record["Topic Name"].S)
                .filter((topic, index, self) => self.indexOf(topic) === index)
                .map((topic, index) => (
                  <option key={index} value={topic}>
                    {topic}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ flex: 1, marginLeft: "10px" }}>
            <label
              htmlFor="scenarioId"
              style={{
                display: "block",
                marginBottom: "10px",
                fontSize: "20px",
              }}
            >
              Select Scenario:
            </label>
            <select
              id="scenarioId"
              value={selectedScenarioId}
              onChange={(e) => this.handleScenarioIdChange(e.target.value)}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#fff",
              }}
            >
              <option value="">Select Scenario</option>
              {records
                .filter((record) => record["Topic Name"].S === selectedTopic)
                .map((record, index) => (
                  <option key={index} value={record["Scenario ID"].S}>
                    {record["Scenario ID"].S + " - " + record["Description"].S}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <hr />
        <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>
          Selected Record
        </h2>

        {selectedRecord && (
          <JsonView
            src={selectedRecord}
            collapsed={2}
            enableClipboard={false}
            onEdit={this.handleEdit}
            onAdd={this.handleEdit}
            onDelete={this.handleEdit}
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #ccc",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              maxWidth: "100%",
              whiteSpace: "pre-wrap",
              overflowX: "auto",
              fontSize: "11px",
              wordBreak: "break-all",
            }}
            className="github"
          />
        )}
      </div>
    );
  }
}

export default JSONViewEditPage;
